import React, {
    MutableRefObject,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import _, { Dictionary, isEmpty, isEqual, kebabCase, keys, last, map, values, keyBy, some } from 'lodash';
import { connect } from 'react-redux';
import {
    AssessmentNotice,
    Assessor,
    ReduxState,
    State,
} from '../../interface';
import InitTable, { Query } from '../Table/Table';
import Loader, { LoaderIndicator } from '../Loader/Loader';
import { InputType } from '../Table/components/Editor/Editor';
import { AssessmentNoticeStatus, RETURNS_STATUSES, TOOLTIP_SECTIONS } from '../../enums';
import useSelectedSystemTaxYear from '../../hook/useSelectedSystemTaxYear';
import style from './AssessmentNoticeTable.module.scss';
import { getAllAssessmentNotices, getAssessorsByAccount, getCompanies, getLegalEntities, getProperties, bulkUpdateAssessmentNotices, getErrorMessage } from '../../services';
import classNames from 'classnames';
import useUserProfile from '../../hook/useUserProfile';
import TableMenu, { MenuItem } from '../TableMenu/TableMenu';
import { useNavigate } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import { formatDate } from '../../utils/stringUtil';
import { ratioBackendToFrontend } from '../../utils/formatNumber';
import FieldDate from '../FieldDate/FieldDate';
import TooltipWrapper from '../TooltipWrapper/TooltipWrapper';
import { ToastMessageRefType } from '../ToastMessage/ToastMessage';

const { Table, Column, TopBar } = InitTable<AssessmentNotice>();


interface Props {
    states: State[];
    includeReturnsWithNoAssets: boolean;
    toastRef: MutableRefObject<ToastMessageRefType | null>;
}

const RETURN_UNFILED_STATUSES = RETURNS_STATUSES.filter(status => status !== 'Filed');

function AssessmentNoticeTable(props: Props) {
    const {
        states,
        includeReturnsWithNoAssets,
        toastRef
    } = props;
    const navigate = useNavigate();
    const [assessmentNotices, setAssessmentNotices] = useState<AssessmentNotice[]>([]);
    const [noticesLength, setNoticesLength] = useState<number>(0);
    const [options, setOptions] = useState<Dictionary<unknown[]>>({});
    const [fetchingData, setFetchingData] = useState(false);
    const [fetchingAllAssessors, setFetchingAllAssessors] = useState(false);
    const [fetchingCompanies, setFetchingCompanies] = useState(false);
    const [fetchingEntities, setFetchingEntities] = useState(false);
    const [fetchingProperties, setFetchingProperties] = useState(false);
    const [assessors, setAssessors] = useState<Assessor[]>([]);
    const selectedSystemTaxYear = useSelectedSystemTaxYear();
    const [query, setQuery] = useState<Query>({});
    const lastQueryRef = useRef<Dictionary<unknown>>(undefined);
    const userProfile = useUserProfile();
    const accountId = userProfile?.userAccountId;

    useEffect(() => {
        if (accountId) {
            setFetchingAllAssessors(true);
            getAssessorsByAccount({ userAccountId: accountId.toString() })
                .then((d) => {
                    setAssessors(
                        d.data.items.map((item: Assessor) => ({
                            id: item.id,
                            label: item.name,
                            name: item.name,
                            value: item.id,
                        })),
                    );
                    setFetchingAllAssessors(false);
                })
                .catch(() => {
                    setFetchingAllAssessors(false);
                });
        }
    }, [accountId]);

    const responseToOptions = (res: AxiosResponse) =>
        map(res.data.items, (x: { id: unknown; name: string; number: string }) => ({
            value: x.id,
            label: x.name ? x.name : '-',
            number: x.number,
        }));
    const addResponseOptions = (key: string, res: AxiosResponse) =>
        setOptions((current) => ({
            ...current,
            [key]: responseToOptions(res),
        }));

    useEffect(() => {
        // Fetch options for company, legal entity, property filters
        setFetchingCompanies(true);
        getCompanies()
            .then((res) => {
                addResponseOptions('companyIds', res);
                setFetchingCompanies(false);
            })
            .catch(() => {
                setFetchingCompanies(false);
            });

        setFetchingEntities(true);
        getLegalEntities()
            .then((res) => {
                addResponseOptions('legalEntityIds', res);
                setFetchingEntities(false);
            })
            .catch(() => {
                setFetchingEntities(false);
            });

        setFetchingProperties(true);
        getProperties()
            .then((res) => {
                addResponseOptions('propertyIds', res);
                setFetchingProperties(false);
            })
            .catch(() => {
                setFetchingProperties(false);
            });
    }, []);

    useEffect(() => {
        fetchData(_.noop);
    }, [query, selectedSystemTaxYear.taxYear, includeReturnsWithNoAssets]);

    const fetchData = (onComplete: () => void) => {
        let returnStatus = '';
        if (query?.filter?.['return.status']) {
            returnStatus = (query?.filter?.['return.status']) === 'Filed' ? 
                'Filed' : 
                RETURN_UNFILED_STATUSES.join(',');
        }
        const formattedQuery = {
            page: query?.pagination?.page || 1,
            perPage: query?.pagination?.pageSize ?? 10,
            sortBy: last(keys(query.order)),
            sortDirection: last(values(query.order)),
            text: query?.search,
            taxYear: selectedSystemTaxYear.taxYear ?? '',
            statuses: (query?.filter?.noticeStatus as string[])?.join(','),
            returnStatuses: returnStatus,
            companyIds: (query?.filter?.companyIds as number[])?.join(','),
            legalEntityIds: (query?.filter?.legalEntityIds as number[])?.join(','),
            propertyIds: (query?.filter?.propertyIds as string[])?.join(','),
            stateIds: (query?.filter?.stateIds as number[])?.join(','),
            assessorIds: (query?.filter?.assessorIds as number[])?.join(','),
            appealDeadlineRange: query?.filter?.['appealDeadline'] as string[],
            includeReturnsWithNoAssets,
        };

        if (!isEqual(formattedQuery, lastQueryRef.current)) {
            lastQueryRef.current = formattedQuery;
            setFetchingData(true);
            getAllAssessmentNotices(formattedQuery)
                .then((res) => {
                    setAssessmentNotices(res.data.items);
                    setNoticesLength(res.data.total);
                    setSelected([]);
                    setFetchingData(false);
                    onComplete();
                })
                .catch(() => {
                    setFetchingData(false);
                    onComplete();
                });
        } else {
            onComplete();
        }
    };

    const formatNameAndNumber = (name?: string, number?: string) => {
        return _.compact([name, number && `${number}`]).join(' | ');
    };

    const getMenuItems = ({ noticeStatus, id }: AssessmentNotice): MenuItem[][] => {
        const actions = [
            noticeStatus === AssessmentNoticeStatus.Missing && {
                'Enter Notice Values': () => {
                    navigate(`/taxes/editAssessmentNotice/${id}`);
                },
            },
            [AssessmentNoticeStatus.Appealed, AssessmentNoticeStatus.Accepted].includes(noticeStatus) && {
                'Edit Notice Values': () => {
                    navigate(`/taxes/editAssessmentNotice/${id}`);
                }
            },
            noticeStatus === AssessmentNoticeStatus.Settled && {
                'Edit Settled Values': () => {
                    navigate(`/taxes/editAssessmentNotice/${id}`);
                },
            },
        ];

        return [
            _.map(_.assign({}, ..._.compact(actions)), (onClick, label) => {
                return {
                    variant: 'default',
                    tooltip: {
                        section: TOOLTIP_SECTIONS.TableRowAction,
                        keyword: label,
                    },
                    label,
                    onClick,
                } as MenuItem;
            })
        ].filter((arr) => !isEmpty(arr));
    };

    // Bulk actions
    const [selected, setSelected] = useState<AssessmentNotice[]>([]);
    const selectedIds = useMemo(
        () => (_.compact(selected).length ? selected.map((item) => item?.id) : []),
        [selected],
    );
    const canSaveSelected = useMemo(
        () =>
            selected.length > 0 &&
            !some(selected, (x) =>
                [
                    AssessmentNoticeStatus.Settled,
                    AssessmentNoticeStatus.Accepted,
                ].includes(x?.noticeStatus as AssessmentNoticeStatus),
            ),
        [selected],
    );
    const [selectedItemsAppealDealine, setSelectedItemsAppealDealine] = useState<string | null>(null);
    const [savingAssessmentNotices, setSavingAssessmentNotices] = useState(false);

    const bulkSaveAssessmentNotices = () => {
        setSavingAssessmentNotices(true);
        bulkUpdateAssessmentNotices(selectedIds.join(','), selectedItemsAppealDealine)
            .then(() => {
                toastRef.current?.showSuccessToast('Assessment Notices saved successfully');
                setSelected([]);
                setSelectedItemsAppealDealine(null);
                lastQueryRef.current = undefined;
                fetchData(_.noop);
                setSavingAssessmentNotices(false);
                
            })
            .catch((e) => {
                toastRef.current?.showErrorToast(getErrorMessage(e));
                setSavingAssessmentNotices(false);
            });
    };

    useEffect(() => {
        const byId = keyBy(assessmentNotices, (x) => x.id);
        setSelected(selected.filter(x => x.id && byId[x.id]));
    }, [assessmentNotices]);

    return (
        <div className={style['table-layout']}>
            <Table
                id={'assessmentNotices'}
                rows={assessmentNotices}
                onQueryChanged={(x) => setQuery(x)}
                paginate
                sortable
                selectable={{ selected, onChange: setSelected }}
                searchable='Search accounts...'
                totalRows={noticesLength}
                dynamicColumns
                loading={fetchingData}
            >
                <TopBar>
                    {selected.length > 0 && (
                        <div className={style['selected-items-action']}>
                            <TooltipWrapper
                                tooltipSection={TOOLTIP_SECTIONS.Common}
                                tooltipKey='Update Appeal Deadlines'
                            >
                                <label>Update Appeal Deadlines to</label>
                            </TooltipWrapper>
                            <FieldDate
                                classnames={style['field-select']}
                                labelText=''
                                primaryFormat='MM/DD/YYYY'
                                selectValue={selectedItemsAppealDealine ?? ''}
                                onSelect={(value) => {
                                    setSelectedItemsAppealDealine(value ?? null);
                                }}
                            />
                            <TooltipWrapper
                                tooltipSection={TOOLTIP_SECTIONS.PageAction}
                                tooltipKey='Save'
                            >
                                <button
                                    className='primary'
                                    disabled={savingAssessmentNotices || !canSaveSelected}
                                    onClick={() => bulkSaveAssessmentNotices()}
                                >
                                    Save ({selected.length})
                                    {savingAssessmentNotices ? (
                                        <LoaderIndicator
                                            className='button-loading'
                                            loading={true}
                                        />
                                    ) : null}
                                </button>
                            </TooltipWrapper>
                            <TooltipWrapper
                                tooltipSection={TOOLTIP_SECTIONS.PageAction}
                                tooltipKey='Cancel'
                            >
                                <button
                                    className='secondary'
                                    onClick={() => {
                                        setSelected([]);
                                        setSelectedItemsAppealDealine(null);
                                    }}
                                >
                                    Cancel
                                </button>
                            </TooltipWrapper>
                        </div>
                    )}
                </TopBar>
                <Column
                    label='Notice Status'
                    prop='noticeStatus'
                    filterable
                    editor={{
                        type: InputType.Select,
                        options: _.map(AssessmentNoticeStatus, (status) => status),
                        multi: true,
                    }}
                >
                    {(status: string) => (
                        <span className={classNames('badge-status', kebabCase(status))}>
                            {status}
                        </span>
                    )}
                </Column>

                <Column
                    label='State'
                    id='return.assessorAccount.property.address.state.abbreviation'
                    prop='return.assessorAccount.property.address.state.abbreviation'
                    accessor={(notice) =>
                        notice.return?.status === 'Filed' ?
                            notice.return?.assessorAccountData?.property.address.state?.abbreviation ?? '' :
                            notice.return?.assessorAccount?.property.address.state?.abbreviation ?? ''
                    }
                />

                <Column
                    id='return.assessorAccount.assessor.name'
                    label='Assessor'
                    accessor={(notice) =>
                        notice.return.status === 'Filed' ?
                            notice.return.assessorAccountData?.assessor?.name ?? '' :
                            notice.return.assessorAccount?.assessor?.name ?? ''
                    }
                ></Column>

                <Column
                    id='return.assessorAccount.number'
                    label='Assessor account number'
                    accessor={(notice) =>
                        notice.return.status === 'Filed' ?
                            notice.return.assessorAccountData?.number ?? '' :
                            notice.return.assessorAccount?.number ?? ''
                    }
                ></Column>

                <Column
                    label='Return Status'
                    prop='return.status'
                    filterable
                    editor={{
                        type: InputType.Select,
                        options: ['Filed', 'Unfiled'],
                        multi: false,
                    }}
                >
                    {(status: string) => {
                        const reducedStatus = status === 'Filed' ? 'Filed' : 'Unfiled';
                        return (
                            <span className={classNames('badge-status', kebabCase(reducedStatus))}>
                                {reducedStatus}
                            </span>
                        )
                    }}
                </Column>

                <Column
                    id='return.totalReportedCost'
                    label='Reported cost'
                    cssClasses='text-align-right'
                >
                    {(notice: AssessmentNotice) => (
                        <div>
                            {Math.round(notice.return?.totalReportedCost) || ''}
                        </div>
                    )}
                </Column>

                <Column
                    id='return.totalAssessedValue'
                    label='Reported assessed value'
                    cssClasses='text-align-right'
                >
                    {(notice: AssessmentNotice) => (
                        <div>
                            {Math.round(notice.return?.totalAssessedValue) || ''}
                        </div>
                    )}
                </Column>

                <Column
                    id='noticeAssessedValue'
                    label='Notice Assessed Value'
                    accessor={(notice) => notice.noticeAssessedValue}
                    cssClasses='text-align-right'
                ></Column>

                <Column
                    id='appealDeadline'
                    prop='appealDeadline'
                    label='Appeal Deadline'
                    accessor={(notice) => notice.appealDeadline}
                    filterable
                    editor={{
                        type: InputType.DateRange,
                        label: ['Appeal deadline From Date', 'Appeal deadline To Date'],
                    }}
                    cssClasses='text-align-right'
                >
                    {formatDate}
                </Column>

                <Column
                    id='finalAssessedValue'
                    label='Final Assessed Value'
                    accessor={(notice) => notice.finalAssessedValue}
                    cssClasses='text-align-right'
                ></Column>

                <Column
                    id='return.assessorAccount.property.company'
                    // prop='return.assessorAccount.property.company'
                    label='Company Name & Number'
                    hidden
                >
                    {(notice: AssessmentNotice) => {
                        const company = notice.return?.assessorAccount?.property?.company;
                        return formatNameAndNumber(company?.name, company?.number)
                    }}
                </Column>

                <Column
                    id='return.assessorAccount.property.legalEntity'
                    // prop='return.assessorAccount.property.legalEntity'
                    label='Legal Entity Name & Number'
                    hidden
                >
                    {(notice: AssessmentNotice) => {
                        const legalEntity = notice.return?.assessorAccount?.property?.legalEntity;
                        return formatNameAndNumber(legalEntity?.name, legalEntity?.number)
                    }}
                </Column>

                <Column
                    id='return.assessorAccount.property'
                    // prop='return.assessorAccount.property'
                    label='Property Name & Number'
                    hidden
                >
                    {(notice: AssessmentNotice) => {
                        const property = notice.return?.assessorAccount?.property;
                        return formatNameAndNumber(property?.name, property?.number)
                    }}
                </Column>

                <Column
                    id='return.assessorAccountData.assessor.assessorTaxYears[0].assessmentRatio'
                    label='Assessment Ratio'
                    cssClasses='text-align-right'
                    accessor={(notice) =>
                        notice.return.assessorAccountData?.assessor?.taxYears[0]?.assessmentRatio ?
                            `${ratioBackendToFrontend(notice.return.assessorAccountData?.assessor?.taxYears[0]?.assessmentRatio)}%`
                            : ''
                    }
                    hidden
                ></Column>

                <Column id='actions' label='Actions' sortable={false}>
                    {(item: AssessmentNotice) => (
                        <div className={style['actions-column-cell']}>
                            <TableMenu
                                toggle={
                                    <button type='button'>
                                        <i className={style['icon-3-dots']} />
                                    </button>
                                }
                                menuItems={getMenuItems(item)}
                            />
                        </div>
                    )}
                </Column>

                {/* Company name filter */}
                <Column
                    id='companyIds'
                    hidden='always'
                    label={['Company', 'Select one or more companies']}
                    filterable
                    editor={{
                        type: InputType.Select,
                        options: options['companyIds'],
                        getLabel: (x) => `${_.get(x, 'label')} | ${_.get(x, 'number')}`,
                        multi: true,
                    }}
                />

                {/* Legal Entity filter */}
                <Column
                    id='legalEntityIds'
                    hidden='always'
                    label={['Legal Entity', 'Select one or more legal entities']}
                    filterable
                    editor={{
                        type: InputType.Select,
                        options: options['legalEntityIds'],
                        getLabel: (x) => `${_.get(x, 'label')} | ${_.get(x, 'number')}`,
                        multi: true,
                    }}
                />

                {/* Property Number filter */}
                <Column
                    id='propertyIds'
                    hidden='always'
                    label={['Property', 'Select one or more properties']}
                    filterable
                    editor={{
                        type: InputType.Select,
                        options: options['propertyIds'],
                        getLabel: (x) => `${_.get(x, 'label')} | ${_.get(x, 'number')}`,
                        multi: true,
                    }}
                />

                {/* State filter */}
                <Column
                    id='stateIds'
                    hidden='always'
                    label={['State', 'Select one or more states']}
                    filterable
                    editor={{
                        type: InputType.Select,
                        options: states,
                        getLabel: (x) => (x as State).abbreviation,
                        getValue: (x) => (x as State).id,
                        multi: true,
                    }}
                />

                {/* Assessor filter */}
                <Column
                    id='assessorIds'
                    hidden='always'
                    label={['Assessor', 'Select one or more assessor names']}
                    filterable
                    editor={{
                        type: InputType.Select,
                        options: assessors,
                        multi: true,
                    }}
                />

            </Table>

            <Loader isOpen={
                fetchingData
                || fetchingAllAssessors
                || fetchingCompanies
                || fetchingEntities
                || fetchingProperties
                || savingAssessmentNotices
            } />
        </div>
    )
}

const mapStateToProps = (state: ReduxState) => ({
    states: state.states,
});

export default connect(mapStateToProps)(AssessmentNoticeTable);